import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Buildings } from '@phosphor-icons/react';
import { money, pct, changeColor, relativeDay, shortDate } from './investorFormat';

const STATUS_LABEL = {
  pending: 'Processing',
  approved: 'Awaiting payment',
  paid: 'Processing',
  allocated: 'Active',
  active: 'Active',
};

/** An owned investment: what I own and how it is doing. */
export const InvestmentCard = ({ holding }) => {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(`/app/portfolio/${holding.security_id}`)}
      className="w-full text-left bg-white rounded-2xl border border-black/[0.05] overflow-hidden hover:border-primary/40 transition-colors"
      data-testid={`investment-card-${holding.security_id}`}
    >
      <div className="flex">
        {holding.image ? (
          <img src={holding.image} alt={holding.project_title} className="w-24 sm:w-32 object-cover flex-shrink-0" />
        ) : (
          <div className="w-24 sm:w-32 bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Buildings size={22} className="text-slate-400" weight="duotone" />
          </div>
        )}

        <div className="flex-1 min-w-0 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium truncate">{holding.project_title}</p>
              <p className="text-xs text-foreground-muted truncate mt-0.5">
                {[holding.location, holding.strategy].filter(Boolean).join(' · ')}
              </p>
            </div>
            <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-md px-2 py-0.5 whitespace-nowrap">
              {STATUS_LABEL[holding.investment_status] || 'Active'}
            </span>
          </div>

          <div className="mt-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-[26px] leading-none font-semibold tracking-tight tabular-nums" data-testid={`investment-value-${holding.security_id}`}>
                {money(holding.current_value)}
              </p>
              <p className="text-xs text-foreground-muted mt-1.5">
                Current value · {money(holding.invested)} invested
              </p>
            </div>
            <div className="text-right">
              <p className={`text-sm font-medium tabular-nums ${changeColor(holding.unrealized_change)}`}>
                {money(holding.unrealized_change, { signed: true })}
              </p>
              <p className={`text-xs tabular-nums ${changeColor(holding.unrealized_percent)}`}>
                {pct(holding.unrealized_percent, { signed: true })}
              </p>
            </div>
          </div>

          <div className="mt-3.5 pt-3 border-t border-black/[0.05] flex items-center justify-between text-xs text-foreground-muted">
            <span className="tabular-nums">
              {holding.units} shares
              {holding.income_received > 0 ? ` · ${money(holding.income_received)} received` : ''}
            </span>
            <span className="text-primary font-medium inline-flex items-center gap-1">
              View investment <ArrowRight size={12} weight="bold" />
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

/** Pending order card - psychologically "I am buying", not yet "I own". */
export const OrderCard = ({ order }) => (
  <div className="bg-white rounded-2xl border border-black/[0.05] px-5 py-4" data-testid={`order-card-${order.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium truncate">{order.project_title || 'Investment'}</p>
        <p className="text-xs text-foreground-muted mt-0.5">
          {order.units} shares · {money(order.amount)} · started {relativeDay(order.created_at)}
        </p>
      </div>
      <span className="text-[11px] font-medium text-amber-700 bg-amber-50 rounded-md px-2 py-0.5 whitespace-nowrap">
        {order.status_label}
      </span>
    </div>
  </div>
);

const KIND_TONE = {
  returns: 'text-emerald-600',
  investments: 'text-slate-900',
};

/** Consolidated investor activity list. */
export const ActivityFeed = ({ events, emptyMessage = 'Your activity will appear here as your portfolio develops.' }) => {
  if (!events || events.length === 0) {
    return <p className="text-sm text-foreground-muted px-5 py-6">{emptyMessage}</p>;
  }

  return (
    <div className="divide-y divide-black/[0.05]" data-testid="investor-activity-feed">
      {events.map((event, i) => (
        <div key={`${event.date}-${i}`} className="px-5 py-3.5 flex items-start gap-4" data-testid="investor-activity-item">
          <span className="text-xs text-foreground-muted w-20 flex-shrink-0 pt-0.5">{relativeDay(event.date)}</span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm">{event.title}</span>
            {event.subtitle && <span className="block text-xs text-foreground-muted truncate">{event.subtitle}</span>}
          </span>
          {event.amount ? (
            <span className={`text-sm font-medium tabular-nums flex-shrink-0 ${KIND_TONE[event.kind] || ''}`}>
              {money(event.amount, { signed: true })}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
};

/** Returns / income panel. Paid vs expected is never blurred. */
export const ReturnsPanel = ({ portfolio }) => (
  <div className="px-5 py-5" data-testid="returns-panel">
    {portfolio.income_received > 0 ? (
      <>
        <p className="text-[32px] leading-none font-semibold tracking-tight text-emerald-600 tabular-nums">
          {money(portfolio.income_received)}
        </p>
        <p className="text-sm text-foreground-muted mt-1.5">Total received</p>
        {portfolio.last_payment && (
          <p className="text-sm text-foreground-muted mt-4">
            Last payment {money(portfolio.last_payment)}
            {portfolio.last_payment_date ? ` on ${shortDate(portfolio.last_payment_date)}` : ''}
          </p>
        )}
      </>
    ) : (
      <>
        <p className="text-sm font-medium">No returns received yet</p>
        <p className="text-sm text-foreground-muted mt-1.5">
          Your investment timeline and future distributions will appear here. Projected returns are estimates, not
          money already earned.
        </p>
      </>
    )}
  </div>
);
